// Media store — the creator's asset library + uploads. Uploads route by
// size (lib/github-media.ts) straight to the public repo from the
// browser; the resulting reference is recorded in OPFS-SQLite.

import { create } from "zustand";
import { listMedia, addMedia, deleteMedia, type MediaItem } from "../lib/storage/media";
import { getOnboarding } from "../lib/storage/onboarding";
import { getSecret } from "../lib/secrets";
import { uploadMedia } from "../lib/github-media";

interface MediaState {
  sub: string | null;
  items: MediaItem[];
  uploading: boolean;
  progress: string | null;
  error: string | null;

  init: (googleSub: string) => Promise<void>;
  upload: (files: FileList | File[]) => Promise<void>;
  remove: (id: string) => Promise<void>;
  clearError: () => void;
}

export const useMediaStore = create<MediaState>((set, get) => ({
  sub: null,
  items: [],
  uploading: false,
  progress: null,
  error: null,

  init: async (googleSub) => {
    set({ sub: googleSub, items: await listMedia(googleSub) });
  },

  upload: async (files) => {
    const sub = get().sub;
    if (!sub) return;
    const token = getSecret("github_token", sub);
    const onb = await getOnboarding(sub);
    if (!token || !onb?.public_repo) {
      set({ error: "Finish onboarding (connect GitHub + create repos) before uploading media." });
      return;
    }
    const list = Array.from(files);
    if (list.length === 0) return;
    set({ uploading: true, error: null });
    try {
      for (let i = 0; i < list.length; i++) {
        set({ progress: `uploading ${i + 1}/${list.length}: ${list[i].name}` });
        const ref = await uploadMedia(token, onb.public_repo, list[i]);
        await addMedia(sub, ref);
      }
      set({ items: await listMedia(sub), uploading: false, progress: null });
    } catch (e) {
      set({ uploading: false, progress: null, error: e instanceof Error ? e.message : String(e) });
    }
  },

  remove: async (id) => {
    await deleteMedia(id);
    const sub = get().sub;
    if (sub) set({ items: await listMedia(sub) });
  },

  clearError: () => set({ error: null }),
}));
